package gr.tech.GRAttestation.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.GRAttestation.components.inputs.StringInput
import gr.tech.GRAttestation.components.inputs.ValidationTrigger
import gr.tech.GRAttestation.components.layouts.StoreUserPageLayout
import gr.tech.GRAttestation.components.widgets.loadingSpinner
import gr.tech.GRAttestation.infrastructure.api.storeUserLoginWithCredentials
import gr.tech.GRAttestation.infrastructure.session.StoreUserSession
import gr.tech.GRAttestation.infrastructure.validations.validateEmail
import gr.tech.GRAttestation.models.user.auth.UserLoginModel
import gr.tech.GRAttestation.navigation.SitePage
import gr.tech.GRAttestation.styles.*
import gr.tech.GRAttestation.styles.grstyles.Theme
import gr.tech.GRAttestation.styles.store.PortalSmallTextStyle
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.events.KeyboardEvent

@Page()
@Composable
fun LoginPage() {
    StoreUserPageLayout(
        title = "Login",
        redirectIfLoggedIn = SitePage.Dashboard.route,
        requiredRoles = emptyList()
    ) {
        LoginScreen()
    }
}

/*
    Developer Notes
    Trying to get enter on login page to work, so I am changing the inputs to be 'OnChange'
    - Don't need validation frontend here, it tells people the format of our passwords and emails
    - If anything, check box isn't empty onSubmit.
 */
@Composable
fun LoginScreen() {
    val scope = rememberCoroutineScope()
    val context = rememberPageContext()

    var errorText by remember { mutableStateOf("") }
    var signInPressed by remember { mutableStateOf(false) }

    /* Input Data */
    var emailInput by remember { mutableStateOf("") }
    var passwordInput by remember { mutableStateOf("") }

    fun handleLogin() {
        val emailValidation = validateEmail(emailInput)
        if (emailValidation.isNotBlank()) { errorText = emailValidation; return }
        if (passwordInput.isBlank()) { errorText = "Password is required."; return }

        scope.launch {
            signInPressed = true
            errorText = ""

            val loginModel = UserLoginModel(
                email = emailInput,
                password = passwordInput
            )

            val apiResult = storeUserLoginWithCredentials(loginModel)
            if (apiResult.success) {
                errorText = ""
                StoreUserSession.setLoggedIn()
                context.router.navigateTo(SitePage.Dashboard.route)
            } else {
                signInPressed = false
                errorText = apiResult.error.message
            }
        }
    }

    LaunchedEffect(Unit) {
        window.addEventListener("keydown", { evt ->
            val event = evt as KeyboardEvent
            if (event.key == "Enter") {
                event.preventDefault()
                handleLogin()
            }
        })
    }

    Box(
        modifier = AuthBoxStyle.toModifier(),
        contentAlignment = Alignment.Center
    ) {

        Column(modifier = AuthCardStyle.toModifier()) {
            // Card Header
            Column(
                modifier = AuthCardHeaderStyle.toModifier(),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // Logo
                Image(
                    src = "/grlogocenter.png",
                    alt = "Green Rebates Logo",
                    modifier = AuthLogoStyle.toModifier()
                )

                // Title
                H2(attrs = AuthTitleStyle.toModifier().toAttrs()) {
                    Text("Store Rebate Sales Reporting")
                }

                // Subtitle
                P(attrs = AuthSubtitleStyle.toModifier().toAttrs()) {
                    Text("ETO Grow Light Fixture Program 2026")
                }
            }

            // Card Body
            Form(
                attrs = Modifier
                    .fillMaxWidth()
                    .padding(24.px, 26.px)
                    .toAttrs {
                        onSubmit {
                            it.preventDefault()
                            handleLogin()
                        }
                    }
            ) {
                StringInput(
                    direction = FlexDirection.Column,
                    inputId = "EmailInput",
                    labelText = "EMAIL",
                    value = emailInput,
                    hasError = false,
                    showError = false,
                    placeholder = "store@email.com",
                    labelError = "",
                    labelModifier = AuthLabelStyle.toModifier(),
                    inputModifier = AuthInputStyle.toModifier().classNames("mb-2"),
                    minLength = 1,
                    maxLength = 254,
                    validationTrigger = ValidationTrigger.OnChange,
                    onValueChange = { value ->
                        emailInput = value
                        errorText = ""
                    }
                )

                StringInput(
                    direction = FlexDirection.Column,
                    inputId = "PasswordInput",
                    labelText = "PASSWORD",
                    value = passwordInput,
                    hasError = false,
                    showError = false,
                    placeholder = "password",
                    labelError = "",
                    labelModifier = AuthLabelStyle.toModifier(),
                    inputModifier = AuthInputStyle.toModifier().classNames("mb-2"),
                    minLength = 1,
                    maxLength = 254,
                    password = true,
                    validationTrigger = ValidationTrigger.OnChange,
                    onValueChange = { value ->
                        passwordInput = value
                        errorText = ""
                    }
                )

                // Error Message
                if (errorText.isNotEmpty()) {
                    P(attrs = AuthErrorStyle.toModifier().toAttrs()) {
                        Text(errorText)
                    }
                }

                // Sign In Button
                Button(
                    colorPalette = null,
                    enabled = !signInPressed,
                    modifier = AuthButtonStyle.toModifier()
                        .thenIf(
                            condition = signInPressed,
                            other = Modifier.classNames("disabled")
                        )
                        .width(100.percent)
                        .padding(12.px),
                    type = ButtonType.Submit,
                    onClick = { }
                ) {
                    if (signInPressed) {
                        loadingSpinner(modifier = Modifier.color(Colors.White))
                    } else {
                        SpanText(
                            text = "Sign In",
                            modifier = Modifier.fontSize(14.px).fontWeight(600)
                        )
                    }
                }

                // Forgot Password Link
                Box(
                    modifier = Modifier.fillMaxWidth().classNames("mt-3"),
                    contentAlignment = Alignment.Center
                ) {
                    Link(
                        text = "Reset Password",
                        path = SitePage.ForgotPassword.route,
                        modifier = Modifier
                            .fontSize(0.75.cssRem)
                            .color(Theme.DarkGreen.rgb)
                    )
                }

                P(attrs = PortalSmallTextStyle.toModifier().classNames("mt-2", "text-center").toAttrs()) {
                    Text("Having trouble? Contact us at ")
                    A(
                        href = "mailto:retail@greenrebates.com",
                        attrs = Modifier.color(Theme.DarkGreen.rgb).toAttrs()
                    ) {
                        Text("retail@greenrebates.com")
                    }
                    Text(" for assistance.")
                }
            }
        }
    }
}